import { useCallback, useMemo, memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const SunIcon = memo(() => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    role="img"
  >
    <path
      d="M16 24C20.4183 24 24 20.4183 24 16C24 11.5817 20.4183 8 16 8C11.5817 8 8 11.5817 8 16C8 20.4183 11.5817 24 16 24Z"
      stroke="#22C55E"
      strokeWidth="2"
    />
    <path
      d="M16 4V2M16 30V28M4 16H2M30 16H28M7 7L5.5 5.5M25 25L23.5 23.5M25 7L26.5 5.5M7 25L8.5 23.5"
      stroke="#22C55E"
      strokeWidth="2"
    />
  </svg>
));
SunIcon.displayName = "SunIcon";

const DropletIcon = memo(() => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    role="img"
  >
    <path
      d="M16 28C20.4183 28 24 24.4183 24 20C24 15.5817 16 4 16 4C16 4 8 15.5817 8 20C8 24.4183 11.5817 28 16 28Z"
      stroke="#3B82F6"
      strokeWidth="2"
    />
  </svg>
));
DropletIcon.displayName = "DropletIcon";

const BatteryIcon = memo(() => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    role="img"
  >
    <rect
      x="4"
      y="10"
      width="22"
      height="12"
      rx="2"
      stroke="#EAB308"
      strokeWidth="2"
    />
    <path d="M28 14V18" stroke="#EAB308" strokeWidth="2" />
  </svg>
));
BatteryIcon.displayName = "BatteryIcon";

const ChartIcon = memo(() => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden="true"
    role="img"
  >
    <path d="M4 28H28M4 4L12 12L20 4L28 12" stroke="#A855F7" strokeWidth="2" />
  </svg>
));
ChartIcon.displayName = "ChartIcon";

const SimpleIcons = {
  Sun: SunIcon,
  Droplet: DropletIcon,
  Battery: BatteryIcon,
  Chart: ChartIcon,
};

const BenefitCard = memo(({ card, index, delay = 0 }) => {
  const Icon = SimpleIcons[card.icon];

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        duration: 0.6,
        delay,
      },
    },
  };

  return (
    <motion.div
      key={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="bg-gray-800 text-gray-300 p-4 rounded-3xl shadow-lg md:p-5 md:w-[220px]"
    >
      <div className="mb-2 md:mb-3">{Icon ? <Icon /> : null}</div>
      <h3 className="text-md md:text-xl font-bold mb-1 md:mb-2 text-white">
        {card.title}
      </h3>
      <p className="text-sm">{card.description}</p>
    </motion.div>
  );
});
BenefitCard.displayName = "BenefitCard";

BenefitCard.propTypes = {
  card: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  delay: PropTypes.number,
};

const Benefits = ({ benefitsImage }) => {
  const { t } = useTranslation();
  const benefits = useMemo(
    () => t("components.home.benefits", { returnObjects: true }),
    [t]
  );
  const renderRichText = useCallback((textArray) => {
    return textArray.map((item, index) => {
      if (typeof item === "string") {
        return <span key={index}>{item}</span>;
      }
      if (item.br) {
        return <br key={index} className="hidden md:block" />;
      }
      if (item.emphasis) {
        return (
          <span key={index} className="text-green-400">
            {item.text}
          </span>
        );
      }
      return <span key={index}>{item.text}</span>;
    });
  }, []);

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const topCards = useMemo(() => benefits.cards.slice(0, 2), [benefits.cards]);
  const bottomCards = useMemo(
    () => benefits.cards.slice(2, 4),
    [benefits.cards]
  );

  return (
    <section className="relative min-h-[100vh] md:h-[110vh] flex items-center justify-center overflow-hidden bg-gray-900 landscape:max-lg:h-[200vh]">
      <motion.div
        initial={{ scale: 1.1 }}
        whileInView={{ scale: 1 }}
        transition={{ duration: 1.5 }}
        viewport={{ once: true }}
        className="absolute inset-0 z-0 h-full"
        style={{
          backgroundImage: `url(${benefitsImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.6)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-black/30 z-10"
        aria-hidden="true"
      ></div>
      <div className="relative z-20 w-full py-10">
        <div className="flex flex-col items-center gap-8">
          <div className="w-full grid grid-cols-2 auto-rows-fr gap-4 md:flex md:flex-row md:justify-between md:gap-6 max-w-3xl mx-auto px-4 order-2 md:order-1">
            {topCards.map((card, index) => (
              <BenefitCard key={index} card={card} index={index} />
            ))}
          </div>

          <motion.div
            variants={textVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center px-4 order-1 md:order-2"
          >
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 text-white">
              {benefits.heading}
            </h2>
            <p className="text-gray-300 text-md lg:text-lg">
              {renderRichText(benefits.subtitle)}
            </p>
          </motion.div>

          <div className="w-full grid grid-cols-2 auto-rows-fr gap-4 md:flex md:flex-row md:justify-between md:gap-6 max-w-2xl mx-auto px-4 order-3">
            {bottomCards.map((card, index) => (
              <BenefitCard
                key={index}
                card={card}
                index={index}
                delay={0.2 * (index + 1)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

Benefits.propTypes = {
  benefitsImage: PropTypes.string.isRequired,
};

export default memo(Benefits);
